"use client";

import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  Building2,
  Check,
  ChevronDown,
  Loader2,
  Search,
  Users,
} from "lucide-react";
import { Button } from "@/app/_module/components/ui/button";
import { Input } from "@/app/_module/components/ui/input";
import api from "@/app/_module/services/api";
import { getApiUrl } from "@/app/_module/services/config";

export type ScopeMode = "self" | "downline" | "custom";

export type LeadExportScopeResult = {
  mode: ScopeMode;
  customAgencyIds: string[];
  customUserIds: string[];
};

type ScopeData = {
  agencies: Array<{ id: string; name: string; isRoot: boolean }>;
  members: Array<{
    id: string;
    name: string;
    email: string;
    role: string;
    agencyId: string;
    agencyName: string;
  }>;
  subAgencyCount: number;
  userCount: number;
  currentScope?: LeadExportScopeResult;
};

const EASE = [0.22, 1, 0.36, 1] as [number, number, number, number];

/**
 * Inline scope picker that expands under the export toggle.
 *
 * Deliberately NOT a modal: this is a settings decision made in place, and a
 * dialog rips you out of the page you were configuring. Expanding in position
 * keeps the toggle you just flipped visible above it, so the choice stays
 * anchored to the thing it affects.
 */
export function LeadExportScopePanel({
  open,
  agencyId,
  editing = false,
  onCancel,
  onApply,
}: {
  open: boolean;
  agencyId?: string;
  /** True when the lockdown is already on and we are changing WHO it covers. */
  editing?: boolean;
  onCancel: () => void;
  onApply: (scope: LeadExportScopeResult) => void;
}) {
  const [mode, setMode] = useState<ScopeMode>("downline");
  const [data, setData] = useState<ScopeData | null>(null);
  const [loading, setLoading] = useState(false);
  const [stale, setStale] = useState(false);
  const [search, setSearch] = useState("");
  const [pickedAgencies, setPickedAgencies] = useState<string[]>([]);
  const [pickedUsers, setPickedUsers] = useState<string[]>([]);

  useEffect(() => {
    if (!open || !agencyId) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const res: any = await api.get({
          url: getApiUrl(`features/${agencyId}/lead-export-lockdown/scope`),
          auth: true,
        });
        if (cancelled) return;
        const d: ScopeData | null = res?.data?.data ?? res?.data ?? null;
        setData(d);
        const cur = d?.currentScope;
        if (cur) {
          setMode(cur.mode ?? "downline");
          setPickedAgencies(cur.customAgencyIds ?? []);
          setPickedUsers(cur.customUserIds ?? []);
        }
        // `members` was added to this endpoint after `agencies`. A response
        // carrying agencies but no members array means the API is older than
        // the UI — say so instead of rendering a silently empty people list.
        setStale(!!d && !Array.isArray((d as any).members));
      } catch {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, agencyId]);

  const q = search.trim().toLowerCase();
  const agencies = useMemo(
    () =>
      (data?.agencies ?? []).filter(
        (a) => !q || a.name.toLowerCase().includes(q),
      ),
    [data, q],
  );
  const members = useMemo(
    () =>
      (data?.members ?? []).filter(
        (m) =>
          !q ||
          m.name?.toLowerCase().includes(q) ||
          m.email?.toLowerCase().includes(q) ||
          m.agencyName?.toLowerCase().includes(q),
      ),
    [data, q],
  );

  const subs = data?.subAgencyCount ?? 0;
  const people = data?.userCount ?? 0;
  const customEmpty =
    mode === "custom" && !pickedAgencies.length && !pickedUsers.length;

  const toggle = (list: string[], id: string) =>
    list.includes(id) ? list.filter((x) => x !== id) : [...list, id];

  const OPTIONS: Array<{ id: ScopeMode; title: string; body: string }> = [
    {
      id: "self",
      title: "Only my agency",
      body: "Sub-agencies keep their export.",
    },
    {
      id: "downline",
      title: "Everyone below me",
      body: `This agency and all ${subs} sub-${subs === 1 ? "agency" : "agencies"}, including any added later.`,
    },
    {
      id: "custom",
      title: "Custom",
      body: "Pick agencies (covers everyone in them) or single agents.",
    },
  ];

  return (
    <AnimatePresence initial={false}>
      {open && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.28, ease: EASE }}
          className="overflow-hidden"
        >
          <div className="border-primary/30 bg-muted/30 mt-3 space-y-3 rounded-xl border p-3">
            {editing && (
              <p className="text-muted-foreground text-xs">
                Export is currently OFF below you. Adjust the selection any time
                — changes save when you press Save.
              </p>
            )}
            {loading ? (
              <div className="text-muted-foreground flex items-center gap-2 py-3 text-sm">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading your
                organisation…
              </div>
            ) : (
              <>
                <p className="text-sm font-medium">
                  {editing
                    ? "Change who loses CSV export"
                    : "Who should lose CSV export?"}
                </p>

                {/* Segmented scope choice */}
                <div className="grid gap-2 sm:grid-cols-3">
                  {OPTIONS.map((opt) => {
                    const active = mode === opt.id;
                    return (
                      <button
                        key={opt.id}
                        type="button"
                        onClick={() => setMode(opt.id)}
                        className={`group relative rounded-lg border p-2.5 text-left transition ${
                          active
                            ? "border-primary bg-primary/10 shadow-sm"
                            : "hover:border-primary/40 hover:bg-background"
                        }`}
                      >
                        <span className="flex items-center justify-between gap-2">
                          <span className="text-sm font-medium">
                            {opt.title}
                          </span>
                          {active && (
                            <Check className="text-primary h-3.5 w-3.5 shrink-0" />
                          )}
                        </span>
                        <span className="text-muted-foreground mt-0.5 block text-xs leading-snug">
                          {opt.body}
                        </span>
                      </button>
                    );
                  })}
                </div>

                <AnimatePresence initial={false}>
                  {mode === "custom" && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.22, ease: EASE }}
                      className="overflow-hidden"
                    >
                      <div className="space-y-2 pt-1">
                        <div className="relative">
                          <Search className="text-muted-foreground absolute top-2.5 left-2.5 h-4 w-4" />
                          <Input
                            placeholder="Search agencies or agents…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="h-9 pl-8"
                          />
                        </div>

                        <div className="bg-background max-h-56 space-y-3 overflow-y-auto rounded-lg border p-2">
                          <Group
                            icon={<Building2 className="h-3.5 w-3.5" />}
                            label={`Agencies (${agencies.length})`}
                          >
                            {agencies.map((a) => (
                              <Row
                                key={a.id}
                                checked={pickedAgencies.includes(a.id)}
                                onToggle={() =>
                                  setPickedAgencies((p) => toggle(p, a.id))
                                }
                                title={a.name}
                                subtitle={a.isRoot ? "your agency" : undefined}
                              />
                            ))}
                          </Group>

                          <Group
                            icon={<Users className="h-3.5 w-3.5" />}
                            label={`Agents (${members.length})`}
                          >
                            {members.length ? (
                              members.map((m) => (
                                <Row
                                  key={`${m.agencyId}-${m.id}`}
                                  checked={pickedUsers.includes(m.id)}
                                  onToggle={() =>
                                    setPickedUsers((p) => toggle(p, m.id))
                                  }
                                  title={m.name || m.email}
                                  subtitle={`${m.agencyName} · ${m.role}`}
                                />
                              ))
                            ) : (
                              <p className="text-muted-foreground px-2 py-1.5 text-xs">
                                {stale
                                  ? "Your API is out of date — restart the backend to load agents."
                                  : q
                                    ? `No agents match “${search}”.`
                                    : "No agents found in these agencies."}
                              </p>
                            )}
                          </Group>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                <div className="flex items-center justify-between gap-3 pt-1">
                  <p className="text-muted-foreground text-xs">
                    {mode === "self"
                      ? "Only your agency loses export. You keep yours."
                      : mode === "downline"
                        ? `${subs} sub-${subs === 1 ? "agency" : "agencies"} · ${people} ${people === 1 ? "agent" : "agents"}. You keep yours.`
                        : `${pickedAgencies.length} ${pickedAgencies.length === 1 ? "agency" : "agencies"} · ${pickedUsers.length} selected. Anyone in a selected agency is covered, including new joiners.`}
                  </p>
                  <div className="flex shrink-0 gap-2">
                    {!editing && (
                      <Button size="sm" variant="ghost" onClick={onCancel}>
                        Cancel
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="destructive"
                      disabled={customEmpty}
                      onClick={() =>
                        onApply({
                          mode,
                          customAgencyIds:
                            mode === "custom" ? pickedAgencies : [],
                          customUserIds: mode === "custom" ? pickedUsers : [],
                        })
                      }
                    >
                      {customEmpty
                        ? "Select at least one"
                        : editing
                          ? "Save changes"
                          : "Turn off export"}
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function Group({
  icon,
  label,
  children,
}: {
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-0.5">
      <p className="text-muted-foreground flex items-center gap-1.5 px-2 text-[11px] font-semibold tracking-wide uppercase">
        {icon}
        {label}
      </p>
      {children}
    </div>
  );
}

function Row({
  checked,
  onToggle,
  title,
  subtitle,
}: {
  checked: boolean;
  onToggle: () => void;
  title: string;
  subtitle?: string;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={`hover:bg-muted/60 flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left transition ${
        checked ? "bg-primary/10" : ""
      }`}
    >
      <span
        className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border transition ${
          checked
            ? "border-primary bg-primary text-primary-foreground"
            : "border-muted-foreground/40"
        }`}
      >
        {checked && <Check className="h-3 w-3" />}
      </span>
      <span className="min-w-0">
        <span className="block truncate text-sm">{title}</span>
        {subtitle && (
          <span className="text-muted-foreground block truncate text-xs">
            {subtitle}
          </span>
        )}
      </span>
      <ChevronDown className="invisible ml-auto h-3 w-3" />
    </button>
  );
}

export default LeadExportScopePanel;
